import { AIR } from '@vm/shared';
import { family, M } from '../palette';
import { blocks, MapBuilder, patches, planks, solid, speckle, stripes, symPoint, symYaw, yawToward } from './builder';
import { emptyMeta, type CoreRegion, type MapDef, type MapMeta } from './types';

/**
 * Depot Run: a rail depot built from a three-route layout (see docs/maps/depot-run.md).
 *
 *   north lane   long and open        (rifles, marksman)       z 3 - 20 m
 *   centre hall  courtyard + galleries (everything)            x 22 - 42 m, z 20 - 44 m
 *   south lane   short and cluttered  (shotguns, SMGs)         z 44 - 61 m
 *
 * Two walled bases at the ends, each with two lane exits and a gate onto the mid-field. Team 0 is west; the east half is a mirror.
 *
 * Built to the navigation grid the bots use (0.5 m cells, 0.7 m clear, 2.3 m headroom over a step), so nothing here is tighter
 * than 1.4 m and no stair has a ceiling over its lower steps: the maps that were sealed for bots (see tools/map-routes.ts) were
 * all a few centimetres too tight somewhere.
 */
const S = 640;
const G = 32; // ground surface (first air voxel)
const SYM = 'mirrorX' as const;
/** West (team 0) Demolition core: a 1.6 m crystal inside the concrete vault behind the spawn yard. */
const CORE: CoreRegion = { team: 0, x0: 7, y0: G + 4, z0: 312, x1: 23, y1: G + 20, z1: 328 };

/** Centre hall footprint (x mirrors about 320). Floors: ground G, galleries G + 30, roof G + 60. */
const HX0 = 220;
const HZ0 = 200;
const HZ1 = 440;
const FLOOR = 30;

/** Concrete jersey barrier: a knee-high wall that breaks a sightline without blocking a route. */
function barrier(b: MapBuilder, x: number, z: number, alongX: boolean): void {
  const w = alongX ? 30 : 8;
  const d = alongX ? 8 : 30;
  b.box(x, G, z, x + w, G + 12, z + d, M('concrete_3'));
  b.box(x, G + 12, z, x + w, G + 13, z + d, M('concrete_4'));
  b.box(x, G, z, x + w, G + 2, z + d, M('road_yellow'));
}

/** Hazard-striped threshold on the ground in front of an opening. */
function threshold(b: MapBuilder, x0: number, z0: number, x1: number, z1: number, alongX: boolean): void {
  b.box(x0, G - 1, z0, x1, G, z1, stripes(M('road_yellow'), M('paint_black'), 4, alongX ? 'x' : 'z'));
}

function westHalf(b: MapBuilder): void {
  const asphalt = solid(family('asphalt'), 0);
  const concrete = blocks(family('concrete'), 5, 22);
  const tile = blocks([M('tile_white'), M('concrete_1')], 5, 23);
  const wallC = blocks([M('concrete_4'), M('concrete_3')], 5, 31);
  const steel = M('steel_dark_2');
  const trim = M('steel_dark_1');
  const rail = M('metal_1');

  // Surfaces ----------------------------------------------------------------------------------
  b.box(3, G - 1, 190, 125, G, 450, concrete); // base compound floor
  b.box(125, G - 1, 30, 320, G, 200, asphalt); // north lane
  b.box(125, G - 1, 440, 320, G, 610, asphalt); // south lane
  b.box(3, G - 1, 30, 125, G, 190, asphalt);
  b.box(3, G - 1, 450, 125, G, 610, asphalt);
  b.box(125, G - 1, 200, 220, G, 440, concrete); // mid-field
  b.box(HX0 + 5, G - 1, HZ0 + 5, 320, G, HZ1 - 5, tile); // hall floor
  for (let x = 130; x < 320; x += 24) {
    b.box(x, G - 1, 113, x + 12, G, 115, M('road_white'));
    b.box(x, G - 1, 524, x + 12, G, 526, M('road_white'));
  }

  // Perimeter hoarding ------------------------------------------------------------------------
  const ply = planks([M('planks_3')], 0);
  b.box(0, G, 0, 3, G + 24, S, ply);
  b.box(0, G, 0, 320, G + 24, 3, ply);
  b.box(0, G, S - 3, 320, G + 24, S, ply);
  for (let i = 20; i < 320; i += 40) {
    b.box(i, G, 3, i + 2, G + 26, 5, trim);
    b.box(i, G, S - 5, i + 2, G + 26, S - 3, trim);
  }
  for (let i = 20; i < S; i += 40) b.box(3, G, i, 5, G + 26, i + 2, trim);
  b.box(0, G + 20, 0, 320, G + 22, 3, M('paint_orange'));
  b.box(0, G + 20, S - 3, 320, G + 22, S, M('paint_orange'));
  b.box(0, G + 20, 0, 3, G + 22, S, M('paint_orange'));

  // Base compound -----------------------------------------------------------------------------
  // Walls 3 m high with three openings: a lane exit north and south (4 m wide) and a gate onto the mid-field (7 m wide).
  b.box(0, G, 190, 130, G + 30, 195, wallC);
  b.box(0, G, 445, 130, G + 30, 450, wallC);
  b.box(125, G, 195, 130, G + 30, 445, wallC);
  b.clear(72, G, 190, 112, G + 30, 195);
  b.clear(72, G, 445, 112, G + 30, 450);
  b.clear(125, G, 285, 130, G + 30, 355);
  threshold(b, 72, 186, 112, 190, true);
  threshold(b, 72, 450, 112, 454, true);
  threshold(b, 130, 285, 134, 355, false);
  // A baffle inside the gate hides the spawn yard from the mid-field and the lanes. It is wider than the gate (10 m against 7 m) so a
  // slanted line through the gate edge cannot slip past its end (a diagonal from the far side of the mid-field reaches the spawns 1.5 m
  // beyond it), and stands 1.7 m behind the gate wall: players walk round it north or south into a 1.7 m corridor that leads to the gate.
  b.box(104, G, 270, 108, G + 24, 370, M('concrete_2'));
  b.box(104, G + 24, 270, 108, G + 25, 370, M('concrete_4'));
  // The same just inside each lane exit: a screen 1.2 m behind the exit, 5.2 m wide (the exit is 4 m), leaving a 2 m walk-round on both sides.
  // Without them a player at the far end of a lane could see straight through the exit to the spawns, 40 m away.
  b.box(66, G, 207, 118, G + 24, 212, M('concrete_2'));
  b.box(66, G + 24, 207, 118, G + 25, 212, M('concrete_4'));
  b.box(66, G, 428, 118, G + 24, 433, M('concrete_2'));
  b.box(66, G + 24, 428, 118, G + 25, 433, M('concrete_4'));
  // Vault with the team crystal against the back wall, a barred slot facing the gate (Demolition objective; set dressing otherwise).
  b.box(3, G, 302, 27, G + 24, 338, blocks([M('concrete_4')], 5, 84));
  b.box(3, G + 24, 300, 27, G + 26, 340, M('plate'));
  b.box(CORE.x0, CORE.y0, CORE.z0, CORE.x1, CORE.y1, CORE.z1, M('core_red'));
  b.clear(23, G + 8, 316, 27, G + 16, 324);
  for (let z = 316; z < 324; z += 3) b.box(25, G + 8, z, 26, G + 16, z + 1, trim);
  b.box(26, G + 20, 302, 27, G + 21, 338, M('team_red_glow'));
  b.box(26, G, 302, 27, G + 20, 303, M('team_red_glow'));
  b.box(26, G, 337, 27, G + 20, 338, M('team_red_glow'));
  b.box(30, G, 300, 32, G + 40, 340, M('team_red')); // banner
  // Compound clutter along the walls (spawns keep 2 m clear).
  b.sandbags(30, 205, 56, 213, G, 9);
  b.sandbags(30, 427, 56, 435, G, 9);
  b.crate(44, G, 242, 10);
  b.crate(55, G, 244, 8);
  b.crate(44, G, 388, 10);
  b.crate(55, G, 386, 8);
  b.barrel(70, G, 214, M('paint_red'));
  b.barrel(78, G, 218, M('paint_orange'));
  // Containers stand in the corners, clear of the vault (z 30.2-33.8 m): a container's interior is carved out, and one that overlapped the
  // vault took a bite out of the crystal.
  b.container(14, G, 200, false, M('paint_teal'), true);
  b.container(14, G, 380, false, M('paint_orange'), true);
  b.lamp(60, G, 300, 40, M('neon_orange'));
  b.lamp(60, G, 340, 40, M('neon_orange'));

  // Centre hall -------------------------------------------------------------------------------
  const wall = blocks([M('brick_orange'), M('brick_red_3')], 3, 41);
  const slab = solid([M('concrete_3')]);
  // North and south outer walls and the west wall; galleries along both sides with a covered upper floor, an open courtyard between.
  b.box(HX0, G, HZ0, 320, G + FLOOR * 2 + 8, HZ0 + 5, wall);
  b.box(HX0, G, HZ1 - 5, 320, G + FLOOR * 2 + 8, HZ1, wall);
  b.box(HX0, G, HZ0 + 5, HX0 + 5, G + FLOOR * 2 + 2, HZ1 - 5, wall);
  b.box(HX0, G + FLOOR * 2 + 2, HZ0, HX0 + 5, G + FLOOR * 2 + 8, HZ1, trim);
  // Three doors on the west side (2 m wide, 2.6 m tall): north gallery, courtyard, south gallery.
  for (const c of [240, 320, 400]) {
    b.clear(HX0, G, c - 10, HX0 + 5, G + 26, c + 10);
    threshold(b, HX0 - 4, c - 10, HX0, c + 10, false);
  }
  // Upper-floor shooting windows onto both lanes.
  for (const a of [264, 290]) {
    b.clear(a, G + 39, HZ0, a + 18, G + 50, HZ0 + 5);
    b.clear(a, G + 39, HZ1 - 5, a + 18, G + 50, HZ1);
  }
  // Galleries: upper slab over the north and south strips (x 26 to the mirror line and beyond), roof above.
  b.box(266, G + FLOOR - 2, HZ0 + 5, 320, G + FLOOR, 255, slab);
  b.box(266, G + FLOOR - 2, 385, 320, G + FLOOR, HZ1 - 5, slab);
  b.box(HX0, G + FLOOR * 2, HZ0, 320, G + FLOOR * 2 + 2, 255, M('plate'));
  b.box(HX0, G + FLOOR * 2, 385, 320, G + FLOOR * 2 + 2, HZ1, M('plate'));
  // Stairs up to each gallery: from the lobby at the west end, with a 2.0 m clear foot in front of the first step. The lobby has no slab over it, so nothing sits above the lower steps (a roof within 2.3 m of a step made the stair hut on Neon Rooftops impassable for bots).
  // 34 degree flights: the first step is 2.0 m from the lobby wall (x 22.5 m), the run is 4.5 m, and where the flight passes under the gallery slab (it starts at
  // x 26.6 m) the slab is cut over the steps, so the top step is level with the gallery floor at its edge.
  for (const z of [210, 418]) {
    b.clear(266, G + FLOOR - 2, z, 290, G + FLOOR, z + 12);
    b.climb(245, G, z, 'e', 12, FLOOR, slab);
  }
  // Footbridge across the courtyard (4 m wide with the mirror) joining the two galleries, resting on a central pillar.
  b.box(300, G + FLOOR - 2, 255, 320, G + FLOOR, 385, slab);
  b.box(308, G, 308, 320, G + FLOOR - 2, 332, M('concrete_4'));
  b.box(308, G + 4, 308, 320, G + 5, 332, M('road_yellow'));
  // Railings: along the bridge and along the gallery edges (open at the bridge mouths).
  b.box(300, G + FLOOR, 255, 301, G + FLOOR + 10, 385, rail);
  b.box(266, G + FLOOR, 254, 300, G + FLOOR + 10, 255, rail);
  b.box(266, G + FLOOR, 385, 300, G + FLOOR + 10, 386, rail);
  // Courtyard and gallery cover.
  b.crate(244, G, 268, 10);
  b.crate(244, G, 362, 10);
  b.crate(270, G, 280, 8);
  b.crate(270, G, 352, 8);
  b.barrel(236, G, 300, M('paint_blue'));
  b.barrel(236, G, 340, M('paint_teal'));
  b.lamp(262, G, 226, 24, M('neon_cyan'));
  b.lamp(262, G, 414, 24, M('neon_cyan'));

  // Mid-field ---------------------------------------------------------------------------------
  barrier(b, 148, 232, true);
  barrier(b, 148, 400, true);
  barrier(b, 178, 262, false);
  barrier(b, 178, 370, false);
  b.crate(196, G, 214, 10);
  b.crate(196, G, 416, 10);

  // North lane: open, with sparse cover (about 28 m of clear sightline between pieces) --------
  b.cylinder(180, 100, G, G + 50, 22, blocks([M('steel_dark_2'), M('plate')], 3, 14)); // fuel tank
  b.cylinder(180, 100, G + 50, G + 53, 24, trim);
  b.barrel(160, G, 130, M('paint_yellow'));
  barrier(b, 215, 150, true);
  barrier(b, 240, 70, true);
  b.container(262, G, 36, false, M('paint_orange'), true);
  b.sandbags(204, 60, 224, 68, G, 9);
  // Central cargo island on the lane centre line (the mirror completes it).
  b.box(296, G, 105, 320, G + 10, 135, M('concrete_2'));
  b.box(296, G + 10, 105, 320, G + 11, 135, M('concrete_4'));
  b.crate(300, G + 10, 110, 10);
  b.lamp(204, G, 50, 36, M('neon_yellow'));
  b.lamp(204, G, 180, 36, M('neon_yellow'));

  // South lane: short and cluttered (containers, crates, low walls; no sightline over 14 m) ----
  b.container(140, G, 470, true, M('paint_teal'), true);
  b.container(190, G, 540, true, M('paint_blue'), true);
  b.container(240, G, 455, false, M('paint_orange'), true);
  b.container(282, G, 520, false, M('paint_red'), true);
  b.crate(170, G, 520, 10);
  b.crate(181, G, 521, 8);
  b.crate(232, G, 540, 10);
  b.crate(262, G, 580, 12);
  b.crate(150, G, 580, 10);
  barrier(b, 205, 480, true);
  barrier(b, 150, 520, true);
  b.sandbags(255, 560, 275, 568, G, 9);
  b.barrel(215, G, 600, M('paint_orange'));
  b.barrel(224, G, 596, M('paint_yellow'));
  b.lamp(204, G, 450, 36, M('neon_yellow'));
  b.lamp(204, G, 600, 36, M('neon_yellow'));
  void steel;
  void speckle;
}

function build(b: MapBuilder): MapMeta {
  b.ground(G, patches([M('concrete_2'), M('concrete_3')], 0.05, 11), speckle(family('dirt')), speckle([M('rock_1'), M('rock_2')]));
  westHalf(b);
  b.symmetrize(SYM);

  // Metadata ------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, 32, 32);
  // Two corner clusters 6 m or more from the flag at (4, 32): with the flag inside the spawn yard (it was 2-5 m from the spawns) an enemy
  // carrier at the flag killed players as they respawned, 16% of respawns in Capture the Flag.
  const team0: [number, number][] = [[6, 23], [8.5, 23], [6, 25.5], [8.5, 25.5], [6, 38.5], [8.5, 38.5], [6, 41], [8.5, 41]];
  for (const [x, z] of team0) {
    const yaw = yawToward(x, z, 32, 32);
    meta.spawns.push({ team: 0, x, y: gy, z, yaw });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.spawns.push({ team: 1, x: mx, y: gy, z: mz, yaw: symYaw(SYM, yaw) });
  }
  // Free-for-all spawns: lanes, mid-field and the upper galleries, each with its mirror.
  const upper = (x: number, z: number): number => b.standAt(x, z, 8);
  const ffaHalf: [number, number, number][] = [
    [16, 12, gy], [24, 6.5, gy], [27.5, 16, gy], [17, 23.5, gy], [17, 40.5, gy], [16, 52, gy], [24, 57.5, gy], [27.5, 48, gy],
    [28.5, 22.5, upper(28.5, 22.5)], [28.5, 41.5, upper(28.5, 41.5)],
  ];
  for (const [x, z, y] of ffaHalf) {
    meta.spawns.push({ team: -1, x, y, z, yaw: yawToward(x, z, 32, 32) });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.spawns.push({ team: -1, x: mx, y, z: mz, yaw: yawToward(mx, mz, 32, 32) });
  }
  meta.points.push({ id: 'A', name: 'West Gate', x: 17.5, y: gy, z: 32, r: 3.5 });
  meta.points.push({ id: 'B', name: 'Under the Bridge', x: 32, y: gy, z: 28, r: 3.5 });
  meta.points.push({ id: 'C', name: 'East Gate', x: 46.5, y: gy, z: 32, r: 3.5 });
  meta.flags.push({ team: 0, x: 4, y: gy, z: 32 }, { team: 1, x: 60, y: gy, z: 32 });
  meta.cores.push({ ...CORE }, { team: 1, x0: S - CORE.x1, y0: CORE.y0, z0: CORE.z0, x1: S - CORE.x0, y1: CORE.y1, z1: CORE.z1 });
  for (const [x, z, kind] of [
    [9.2, 32, 'health'], [24, 12, 'health'], [24, 52, 'health'], [27.5, 27, 'health'], [27.5, 37, 'health'], [17.5, 26, 'armor'], [17.5, 38, 'armor'],
  ] as const) {
    meta.pickups.push({ kind, x, y: gy, z });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.pickups.push({ kind, x: mx, y: gy, z: mz });
  }

  // Keep the voxels around every spawn clear of clutter and make sure there is floor under it.
  for (const s of meta.spawns) {
    const x = Math.floor(s.x * 10);
    const z = Math.floor(s.z * 10);
    const y = Math.round(s.y * 10);
    b.clear(x - 3, y, z - 3, x + 4, y + 18, z + 4);
    if (b.w.get(x, y - 1, z) === AIR) b.set(x, y - 1, z, M('stone_1'));
  }
  // The outer wall is a thin rim: nothing that moves may stand on it or beyond it (an invisible wall at every height from the rim's inner face).
  b.limitPlayRim(G + 15, 12);
  return meta;
}

export const depotRun: MapDef = {
  id: 'depot_run',
  name: 'Depot Run',
  description: 'A rail depot with three ways across: a long open lane, a cluttered yard and a hall between them.',
  size: [S, 192, S],
  modes: ['tdm', 'ffa', 'dom', 'ctf', 'demolition', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 2024,
  version: 2,
  theme: {
    skyTop: 0x5f98dc,
    skyHorizon: 0xf0dcc2,
    fog: 0xe6d6bf,
    fogNear: 40,
    fogFar: 150,
    sun: [0.5, 0.72, 0.38],
    sunColor: 0xffe9c8,
    sunIntensity: 1.0,
    ambient: 0xc4d4f0,
    ambientIntensity: 0.55,
    outside: 0x8a8478,
    music: 'military',
  },
  build,
};

